// Page turning for the HTML reader: articles, markdown files and EPUBs.
//
// The whole article (header, text, notes) is laid out as CSS columns exactly
// one screen tall, and the article itself becomes a window onto them, one
// page wide — two columns side by side when there is room for two full
// measures. Turning a page scrolls that window sideways by a page. Being
// ordinary scrolling, the browser's own scrollIntoView, find-in-page and
// selection keep working; anything that lands between pages is snapped back
// onto one.
//
// Position is still one number, the page's place in the book as a 0–1 ratio
// (textPagedRatio), so reading progress and the backend are unchanged.

export type TextReading = 'scroll' | 'paged';

const READING_KEY = 'nexus_text_reading';
/** Space between a spread's two columns; also what separates pages offscreen. */
const GAP = 48;
/** How narrow (as a share of the measure) a spread's columns may get before
 *  one column reads better. */
const SPREAD_MIN = 0.75;
/** Bottom margin under the page, so the last line doesn't sit on the edge. */
const BOTTOM = 20;
const TURN_MS = 180;

interface Pager {
  article: HTMLElement;
  page: number;
  count: number;
  /** Width of one turn: the page plus the gap to the next. */
  step: number;
  listeners: AbortController;
  resize: ResizeObserver;
  /** The width last laid out for: the height is our own doing. */
  width: number;
  /** A turn we started is under way; its scroll events are ours, not the browser's. */
  turning: boolean;
  /** Every block of text, in reading order, and which of them opens the
   *  current page: the place a relayout has to come back to. It is found as
   *  each page is shown, since by the time a resize or a new font size can be
   *  noticed the old layout is already gone. */
  blocks: Element[];
  anchor: number;
  /** Exactly where the page begins: the first line's text when the page opens
   *  partway through a paragraph, else the block itself. */
  place: Element | Range | null;
}

let reading: TextReading = readReading();
let pager: Pager | null = null;
let pageListener: ((page: number, count: number) => void) | null = null;
/** Where paging was last turned off, and the scroll that showed it. Lines wrap
 *  differently in the two views, so the scrolling view's top line starts a
 *  few words before that place; turning paging back on without having moved
 *  returns to the place itself rather than creeping back a line each time. */
let leftAt: { place: Element | Range; scrollY: number } | null = null;

function readReading(): TextReading {
  try {
    return localStorage.getItem(READING_KEY) === 'paged' ? 'paged' : 'scroll';
  } catch {
    return 'scroll';
  }
}

export function getTextReading(): TextReading {
  return reading;
}

export function isTextPaged(): boolean {
  return !!pager;
}

/** Called whenever the page, or the number of pages, changes. */
export function onTextPageChange(fn: (page: number, count: number) => void): void {
  pageListener = fn;
}

// ── Layout ────────────────────────────────────────────────────────────────────

/** The measure in pixels, at the article's current font. */
function measurePx(article: HTMLElement): number {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:absolute;visibility:hidden;width:var(--reader-measure)';
  article.append(probe);
  const width = probe.offsetWidth;
  probe.remove();
  return width;
}

/**
 * Size the columns to the window and count the pages. Two passes: the article
 * first takes all the room a spread could use, then settles on one column if
 * that room would squeeze two.
 */
function layout(p: Pager): void {
  const { article } = p;
  article.querySelector(':scope > .page-filler')?.remove();

  const toolbar = document.querySelector('.reader-toolbar');
  const top = toolbar ? toolbar.getBoundingClientRect().bottom : 0;
  const margin = parseFloat(getComputedStyle(article).marginBlockStart) || 0;
  article.style.setProperty('--page-height', `${Math.max(200, window.innerHeight - top - margin - BOTTOM)}px`);

  article.dataset.cols = '2';
  const measure = measurePx(article);
  const cols = article.clientWidth >= 2 * measure * SPREAD_MIN + GAP ? 2 : 1;
  article.dataset.cols = String(cols);

  p.step = article.clientWidth + GAP;
  const column = (article.clientWidth - (cols - 1) * GAP) / cols;
  let columns = Math.max(1, Math.round((article.scrollWidth + GAP) / (column + GAP)));
  // An odd number of columns in a spread would leave the last page unable to
  // scroll fully into place (a scroller stops at its content's edge), so the
  // last column gets an empty partner.
  if (columns % cols) {
    const filler = document.createElement('div');
    filler.className = 'page-filler';
    filler.setAttribute('aria-hidden', 'true');
    article.append(filler);
    columns += cols - (columns % cols);
  }
  p.count = Math.max(1, Math.ceil(columns / cols));
  p.width = article.clientWidth;
}

/** A collapsed range (a caret) has an empty bounding box; its line box is the
 *  first of its client rects. */
function rectOf(target: Element | Range): DOMRect | null {
  const rect = target.getBoundingClientRect();
  if (rect.width || rect.height) return rect;
  return target.getClientRects()[0] ?? null;
}

/** The text under a point, if it's text inside `root`. */
function caretAt(root: HTMLElement, x: number, y: number): Range | null {
  const doc = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  let range: Range | null = null;
  if (doc.caretPositionFromPoint) {
    const position = doc.caretPositionFromPoint(x, y);
    if (position) {
      range = document.createRange();
      range.setStart(position.offsetNode, position.offset);
    }
  } else {
    range = doc.caretRangeFromPoint?.(x, y) ?? null;
  }
  const node = range?.startContainer;
  if (!range || node?.nodeType !== Node.TEXT_NODE || !root.contains(node)) return null;
  // The first character rather than the caret before it: a caret at the
  // start of a wrapped line reports the end of the line above.
  const offset = range.startOffset;
  if (offset < (node.textContent?.length ?? 0)) range.setEnd(node, offset + 1);
  else range.collapse(true);
  return rectOf(range) ? range : null;
}

/** The page an element (or range) starts on; the current one if it has no box. */
function pageOf(p: Pager, target: Element | Range): number {
  const rect = rectOf(target);
  if (!rect) return p.page;
  const origin = p.article.getBoundingClientRect().left + p.article.clientLeft;
  const x = rect.left - origin + p.article.scrollLeft;
  return Math.min(p.count - 1, Math.max(0, Math.floor((x + 1) / p.step)));
}

const BLOCKS = 'p, h1, h2, h3, h4, h5, h6, li, blockquote, pre, figure, img, table, hr, dt, dd';

/**
 * Which block opens the current page. Every block from there on reaches past
 * the page's left edge and none before it does, so the search walks from the
 * last page's answer rather than from the start of the book: a turn costs a
 * page's worth of blocks, not the whole book's.
 */
function findAnchor(p: Pager): number {
  const { blocks } = p;
  if (!blocks.length) return -1;
  const left = p.article.getBoundingClientRect().left + 1;
  const reaches = (k: number): boolean => {
    const rect = blocks[k]!.getBoundingClientRect();
    return rect.width > 0 && rect.right > left;
  };
  let i = Math.min(Math.max(p.anchor, 0), blocks.length - 1);
  if (reaches(i)) {
    while (i > 0 && reaches(i - 1)) i--;
    return i;
  }
  while (i < blocks.length - 1 && !reaches(i)) i++;
  return reaches(i) ? i : -1;
}

/** Where the current page begins (see Pager.place). */
function findPlace(p: Pager): Element | Range | null {
  const block = p.blocks[p.anchor];
  if (!block) return null;
  const view = p.article.getBoundingClientRect();
  const rect = block.getBoundingClientRect();
  if (rect.left >= view.left - 1) return block;
  // Continued from the page before: the text at the top of this page's column.
  return caretAt(p.article, view.left + 2, view.top + 8) ?? block;
}

/** What you are reading in the scrolling view: the text on the line just
 *  under the toolbar, or failing that the first block reaching below it. */
function placeInScroll(content: HTMLElement, line: number): Element | Range | null {
  const left = content.getBoundingClientRect().left;
  const caret = caretAt(content, left + 2, line + 12);
  if (caret) return caret;
  for (const block of content.querySelectorAll(BLOCKS)) {
    const rect = block.getBoundingClientRect();
    if (rect.height && rect.bottom > line + 48) return block;
  }
  return null;
}

/** Show a page. `place` is where it should be taken to begin, when that is
 *  already known — a relayout keeps the original rather than re-finding it
 *  each time, which would creep backwards. */
function show(p: Pager, page: number, animate = 0, place?: Element | Range | null): void {
  const next = Math.min(p.count - 1, Math.max(0, page));
  p.turning = true;
  p.article.scrollLeft = next * p.step;
  // The scroll event this causes arrives after the frame; let it pass first.
  requestAnimationFrame(() => requestAnimationFrame(() => { p.turning = false; }));
  if (animate && next !== p.page && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    p.article.animate(
      [{ opacity: 0.35, transform: `translateX(${animate * 18}px)` }, { opacity: 1, transform: 'none' }],
      { duration: TURN_MS, easing: 'ease-out' },
    );
  }
  const changed = next !== p.page;
  p.page = next;
  p.anchor = findAnchor(p);
  p.place = place ?? findPlace(p);
  if (changed) pageListener?.(p.page + 1, p.count);
}

/** Lay out again (a resize, a new font size), keeping the same text in view. */
export function relayoutText(): void {
  const p = pager;
  if (!p) return;
  const { place } = p;
  const before = p.count;
  layout(p);
  show(p, place ? pageOf(p, place) : p.page, 0, place);
  if (p.count !== before) pageListener?.(p.page + 1, p.count);
}

// ── Turning ───────────────────────────────────────────────────────────────────

export function turnTextPage(direction: 1 | -1): void {
  if (pager) show(pager, pager.page + direction, direction);
}

export function goToTextPage(page: number): void {
  if (pager) show(pager, page - 1);
}

/** Turn to whatever page holds this element or range; nothing if it's already showing. */
export function revealText(target: Element | Range): void {
  if (!pager) return;
  const page = pageOf(pager, target);
  if (page !== pager.page) show(pager, page, page > pager.page ? 1 : -1);
}

/** Whether an element starts on this page or an earlier one — "already reached". */
export function textPageReached(target: Element): boolean {
  return !!pager && pageOf(pager, target) <= pager.page;
}

export function textPagedRatio(): number {
  if (!pager || pager.count <= 1) return 1;
  return pager.page / (pager.count - 1);
}

export function showTextRatio(ratio: number): void {
  if (pager) show(pager, Math.round(Math.min(1, Math.max(0, ratio)) * (pager.count - 1)));
}

// ── Input ─────────────────────────────────────────────────────────────────────

function listen(p: Pager): void {
  const { signal } = p.listeners;
  const { article } = p;

  // Something other than a turn moved the page (find-in-page, a selection
  // dragged past the edge, a focused link): settle onto the page it reached.
  let settle = 0;
  article.addEventListener('scroll', () => {
    if (p.turning) return;
    clearTimeout(settle);
    settle = window.setTimeout(() => show(p, Math.round(article.scrollLeft / p.step)), 120);
  }, { signal, passive: true });

  // Horizontal swipes turn pages on a touch screen.
  let start: { x: number; y: number } | null = null;
  article.addEventListener('pointerdown', (event) => {
    start = event.pointerType === 'touch' ? { x: event.clientX, y: event.clientY } : null;
  }, { signal });
  article.addEventListener('pointercancel', () => { start = null; }, { signal });
  article.addEventListener('pointerup', (event) => {
    if (!start) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    start = null;
    if (Math.abs(dx) > 60 && Math.abs(dy) < Math.abs(dx) / 2) turnTextPage(dx < 0 ? 1 : -1);
  }, { signal });

  // A wheel or trackpad gesture turns one page. Its momentum keeps sending
  // events for a second or so; those belong to the same gesture until a pause.
  let gesture = 0;
  let travelled = 0;
  let turned = false;
  article.addEventListener('wheel', (event) => {
    if (event.ctrlKey) return; // pinch zoom
    event.preventDefault();
    clearTimeout(gesture);
    gesture = window.setTimeout(() => { travelled = 0; turned = false; }, 200);
    if (turned) return;
    travelled += Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (Math.abs(travelled) > 40) {
      turned = true;
      turnTextPage(travelled > 0 ? 1 : -1);
    }
  }, { signal, passive: false });

  window.addEventListener('resize', () => relayoutText(), { signal });
}

// ── Switching ─────────────────────────────────────────────────────────────────

/**
 * Turn paging on for the article now in the reader. With `keepPlace`, the
 * page that opens is the one holding the line you were reading while
 * scrolling; without, the first (the caller restores a saved position).
 */
export function enableTextPaging(article: HTMLElement, content: HTMLElement, { keepPlace = true } = {}): void {
  disableTextPaging({ keepPlace: false });
  const toolbar = document.querySelector('.reader-toolbar');
  const unmoved = leftAt && leftAt.scrollY === window.scrollY && content.contains(leftAt.place instanceof Range
    ? leftAt.place.startContainer : leftAt.place);
  const place = !keepPlace ? null
    : unmoved ? leftAt!.place
    : placeInScroll(content, toolbar?.getBoundingClientRect().bottom ?? 0);
  leftAt = null;

  window.scrollTo(0, 0);
  article.toggleAttribute('data-paged', true);
  const p: Pager = {
    article, page: 0, count: 1, step: 1,
    listeners: new AbortController(),
    resize: new ResizeObserver(() => {
      if (pager === p && article.clientWidth !== p.width) relayoutText();
    }),
    width: 0,
    turning: false,
    blocks: [...article.querySelectorAll(BLOCKS)],
    anchor: 0,
    place: null,
  };
  pager = p;
  layout(p);
  show(p, place ? pageOf(p, place) : 0, 0, place);
  pageListener?.(p.page + 1, p.count);
  listen(p);
  p.resize.observe(article);
}

export function disableTextPaging({ keepPlace = true } = {}): void {
  const p = pager;
  if (!p) return;
  const place = keepPlace ? p.place : null;
  pager = null;
  p.listeners.abort();
  p.resize.disconnect();
  p.article.querySelector(':scope > .page-filler')?.remove();
  p.article.removeAttribute('data-paged');
  p.article.removeAttribute('data-cols');
  p.article.style.removeProperty('--page-height');
  p.article.scrollLeft = 0;
  // Back in the scrolling view, the same line goes just under the toolbar —
  // measured a frame later, once the toolbar has lost its page controls.
  if (place) {
    requestAnimationFrame(() => {
      const rect = rectOf(place);
      const toolbar = document.querySelector('.reader-toolbar');
      if (rect) window.scrollBy(0, rect.top - (toolbar?.getBoundingClientRect().bottom ?? 0) - 8);
      leftAt = { place, scrollY: window.scrollY };
    });
  }
}

export function setTextReading(next: TextReading): void {
  reading = next;
  try {
    localStorage.setItem(READING_KEY, next);
  } catch {
    // Private browsing may refuse; the choice still holds for this session.
  }
}
